import { OnExportReadyCallback } from '@ecosystem/export-utils';
import { ExportFormat, useAnalytics } from '@ecosystem/newron-client-lib';
import { containers } from '@ecosystem/newron-design-system';
import { isEmpty } from 'lodash';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SaleTypeMapping } from '@const/PerformanceType';
import { renderVisualization } from '@data-visualization/utilities/ChartUtils';
import OpportunityReviewBubbleChart from '@data-visualization/opportunity-review-bubble-chart/OpportunityReviewBubbleChart';
import {
  BubbleChartResp,
  OpportunityReviewBoardGroupingPgKpi,
  OpportunityReviewPageResponses,
  RetailerType,
  SaleType,
} from '@models/OpportunityReview';
import { NoResult } from '@pages/NoResult';
import OpportunityReviewHeaderIcon from '@pages/opportunity-review/OpportunityReviewHeaderIcon';
import { OPPORTUNITY_REVIEW_CARD_TITLE } from '@pages/Utilities';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { TableName } from '@utils/page-analytics/model/TableClickTracking';
import {
  FactRevenue,
  FeatureOption,
  FilterKeys,
  Filters,
  FilterTitle,
  Option,
  PageName,
  PageType,
} from '@utils/pageConfig/Types';
import {
  getDefaultValueForDerivablePeriodicity,
  getMonth,
  isFullWeekRangeFormat,
  isMonthRangeFormat,
  isWeekRangePayloadFormat,
  months,
} from '@utils/PeriodUtils';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import OpportunityReviewBoard from '../opportunity-review-board/OpportunityReviewBoard';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { IRootState } from '@state/reducers/Reducers';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { CreateInsightDataFn } from '@ecosystem/newron-client-collections';
import { OpportunityReviewBubbleChartInsight } from '../collections/opportunity-review-bubble-chart/Model';
import { ViewType } from '@hooks/export/Model';
import { WithHiddenListBox } from '@components/design';
import { LimitPgInfo } from '@pages/LimitPgInfo';
import { buildOptions, CATEGORY_FEATURE_OPTION } from '@utils/hook/selector/ShowPerformanceWithinSelector';
import { multiCountryDisabledFilters } from '@utils/multiCountryUtils/MultiCountryUtils';
import { GroupContext } from '@utils/userPreferencesUtils/Types';
import OpportunityReviewFilter from '@components/opportunity-review-filter/OpportunityReviewFilter';
import OpportunityReviewChartFilter from '@components/opportunity-review-filter/OpportunityReviewChartFilter';
import { OpportunityReviewViewState } from '@models/SavedViews';
import useLimitProductGroup from '@utils/hook/explore/UseLimitProductGroup';
import { useOpportunityReviewBoardGroupEventState } from '@components/opportunity-review-group/utils/OpportunityReviewBoardGroupContext';
import ErrorResultMessage from '@pages/ErrorResultMessage';

interface Props {
  data: OpportunityReviewPageResponses;
  appliedProductGroups: Option[];
  loadingBubbleChart: boolean;
  loadingSwotTable: boolean;
  loadMore: boolean;
  selectedPeriod: string;
  selectedTimeFrame: Periodicity;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  resizeChart: () => void;
  saleType: SaleType;
  setSaleType: Dispatch<SetStateAction<SaleType>>;
  retailerType: RetailerType;
  setRetailerType: Dispatch<SetStateAction<RetailerType>>;
  displayTable: boolean;
  setDisplayTable: Dispatch<SetStateAction<boolean>>;
  feature: FeatureOption;
  setFeature: (feat: FeatureOption) => void;
  featureHandler: (feat: FeatureOption) => void;
  onChartDownload: (exportFormats: ExportFormat[]) => Promise<void>;
  isExportButtonDisabled: boolean;
  setIsExportButtonDisabled: Dispatch<SetStateAction<boolean>>;
  onChartExportReady: OnExportReadyCallback;
  setBubbleSliderCurrentPeriod: Dispatch<SetStateAction<string>>;
  volume: string;
  isPeriodicityMonthlyOrWeekly: boolean;
  createInsightDataFn: CreateInsightDataFn<OpportunityReviewBubbleChartInsight>;
  featureFilters: Filters[];
  context: GroupContext;
  groupContent: OpportunityReviewBoardGroupingPgKpi[];
  opportunityReviewState: OpportunityReviewViewState;
  doPreserveSelections: boolean;
  setDoPreserveSelections: Dispatch<SetStateAction<boolean>>;
  totalSize: number;
  isApiError?: boolean;
}

const OpportunityReviewCard: React.FC<Props> = ({
  data,
  appliedProductGroups,
  loadingBubbleChart,
  loadingSwotTable,
  loadMore,
  selectedPeriod,
  selectedTimeFrame,
  selectedGrowthType,
  factRevenue,
  resizeChart,
  saleType,
  setSaleType,
  retailerType,
  setRetailerType,
  displayTable,
  setDisplayTable,
  feature = CATEGORY_FEATURE_OPTION,
  setFeature,
  featureHandler,
  onChartDownload,
  isExportButtonDisabled,
  setIsExportButtonDisabled,
  onChartExportReady,
  setBubbleSliderCurrentPeriod,
  volume,
  isPeriodicityMonthlyOrWeekly,
  createInsightDataFn,
  featureFilters,
  context,
  groupContent,
  opportunityReviewState,
  doPreserveSelections,
  setDoPreserveSelections,
  totalSize,
  isApiError = false,
}) => {
  const [featureValue, setFeatureValue] = useState<string | null>(feature.value);

  const [currentPeriod, setCurrentPeriod] = useState<string>('');
  //Based on this state while exporting Bubble chart as Image & PPT the tooltip, help icon & zoom button will be hidden.
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);

  const chartName = `${OPPORTUNITY_REVIEW_CARD_TITLE} ${displayTable ? ElementType.TABLE : ElementType.BUBBLE_CHART}`;
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const mainFiltersState = useSelector((state: any) => state.mainFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const pagesConfig = usePageConfig();
  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.OPPORTUNITY_REVIEW);
  const { clearSelectedSegments, selectedSegments } = useOpportunityReviewBoardGroupEventState();
  const isLimitPG = useLimitProductGroup(appliedProductGroups?.length, feature);
  const createViewState = useCallback(() => opportunityReviewState, [opportunityReviewState]);

  // If you know how to do this better, then do it, thanks
  useEffect(() => {
    if (feature.label === FilterTitle.CATEGORY && appliedProductGroups?.length === 1) {
      setFeature({
        dataType: 'BRAND',
        feature: 'Brands',
        filterKey: FilterKeys.BRAND,
        label: 'Brands',
        value: 'Brands',
      });
    }
  }, [feature, appliedProductGroups]);

  useEffect(() => {
    setCurrentPeriod(selectedPeriod.toUpperCase());
    setBubbleSliderCurrentPeriod(formatSliderPeriod(selectedPeriod));
  }, [selectedPeriod]);

  const displayContent = () => {
    let content;
    if (isLimitPG) {
      content = LimitPgInfo();
    } else if (displayTable) {
      content = displayOpportunityReviewTable();
    } else {
      content = displayBubbleChart();
    }
    return content;
  };

  const formatSliderPeriod = (period: string) => {
    return getDefaultValueForDerivablePeriodicity(selectedTimeFrame) === Periodicity.Monthly
      ? formatSliderMonthlyPeriod(period)
      : formatSliderWeeklyPeriod(period);
  };

  const formatSliderMonthlyPeriod = (period: string) => {
    if (period.includes('YTD') || period.includes('MAT')) return period;
    if (isMonthRangeFormat(period)) return period;

    // JUL 21
    const monthYear = period.split(' ');
    const month = getMonth(monthYear[0]);
    return `${months[month]} 20${monthYear[1]}`;
  };

  const formatSliderWeeklyPeriod = (period: string) => {
    if (period.includes('YTD') || period.includes('MAT')) return period;
    if (isWeekRangePayloadFormat(period)) return period;
    if (isFullWeekRangeFormat(period)) return period;

    // W37-21
    const weekYear = period.split('-');
    return `${weekYear[0].slice(0, 1)}eek ${weekYear[0].slice(1, 3)} 20${weekYear[1]}`;
  };

  // Display the points of the slider by either market or retailer is fine (they are having the same point = period)
  const buildSliderPoints = () => {
    if (!data.bubbleChartResponse || data.bubbleChartResponse.content.length === 0) {
      return [];
    }

    // change to check on 3rd value as 1st and 2nd value will be YTD and MAT (NEO-19442)
    const marketValues = data.bubbleChartResponse.content[0].market;

    //In V2, the starting index will be 2 because there are YTD and MAT records in index 0 & 1,
    //whereas in V3 (incremental loading), slider has been removed and there will only be
    //one record for the selected period from the timeframe.
    const marketValuesIndex = 0;
    if (
      !marketValues[marketValuesIndex] ||
      (selectedTimeFrame === Periodicity.Monthly && marketValues[marketValuesIndex].period.startsWith('W')) ||
      (selectedTimeFrame === Periodicity.Weekly && !marketValues[marketValuesIndex].period.startsWith('W'))
    ) {
      return [];
    }

    return marketValues.map((market: { period: string }) => {
      return {
        label: formatSliderPeriod(market.period), // Jul 2021 / Week 37 2021
        value: market.period, // JUL 21
      };
    });
  };

  useEffect(() => {
    const sliderPoints = buildSliderPoints();
    const selectedSliderPoint = sliderPoints.find((e) => e.value === currentPeriod);
    if (selectedSliderPoint) {
      setBubbleSliderCurrentPeriod(selectedSliderPoint?.label);
    }
  }, [currentPeriod]);
  /*
                                                                                                                                                                                                  state and useEffect needed so that we can reset the state of the bubble chart even when user has used the legend filter
                                                                                                                                                                                                  eg. i select only SONY in the legend, if i move back one period, it should show me all the brands in the legend again
                                                                                                                                                                                                */
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  useEffect(() => {
    setIsDataChanged(true);
  }, [retailerType, currentPeriod]);

  useEffect(() => {
    const option = filteredOptions.find((o) => o.value === featureValue);
    if (option && option?.value !== feature.value) {
      setFeature(option);

      if (featureHandler) {
        featureHandler(option);
      }
    }
  }, [featureValue]);

  useEffect(() => {
    if (feature.value !== featureValue) {
      setFeatureValue(feature.value);
      setDoPreserveSelections(false);
    }
  }, [feature]);

  const createParamByElementType = (
    elementType: ElementType,
    hasData: boolean,
    componentNameValue: string,
  ): (() => PageElementViewTrackingParam) => {
    return () => {
      return {
        eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
        sideFilters: sideFiltersState.appliedFilters,
        retailerClientState: retailerClientState,
        track: track,
        pageType: PageType.OPPORTUNITY_REVIEW,
        pageName: PageName.OPPORTUNITY_REVIEW,
        componentName: componentNameValue,
        elementType,
        elementKpi: SaleTypeMapping(volume)[saleType],
        mainFilterState: mainFiltersState,
        hasData,
        access: pagesConfig.access,
        initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.OPPORTUNITY_REVIEW, effectiveProductType),
      };
    };
  };

  const { ref: OpportunityReviewBoardComponent } = useInViewTracking(
    createParamByElementType(ElementType.TABLE, !isEmpty(data?.swotTableResponse), TableName.OPPORTUNITY_REVIEW_TABLE),
    { threshold: 0.5, triggerOnce: true },
  );
  const { ref: OpportunityReviewBubbleChartComponent } = useInViewTracking(
    createParamByElementType(
      ElementType.BUBBLE_CHART,
      !isEmpty(data?.bubbleChartResponse),
      TableName.OPPORTUNITY_REVIEW_BUBBLE_CHART,
    ),
    { threshold: 0.3, triggerOnce: true },
  );

  const memoizedTableRowsLength = React.useMemo(() => data.swotTableResponse.length, [data.swotTableResponse]);
  const clearSelectionsIfNoData = () => {
    if (memoizedTableRowsLength === 0) {
      clearSelectedSegments();
    }
  };

  useEffect(() => {
    clearSelectionsIfNoData();
    if (selectedSegments.length > totalSize && doPreserveSelections) {
      setDoPreserveSelections(false);
    }
  }, [memoizedTableRowsLength]);

  const displayBubbleChart = () => {
    return renderVisualization({
      isLoading: loadingBubbleChart,
      fetchedApiData: data.bubbleChartResponse,
      defaultData: new BubbleChartResp(),
      jsxToRender: (
        <>
          {false}
          <div ref={OpportunityReviewBubbleChartComponent}>
            <OpportunityReviewBubbleChart
              data={data.bubbleChartResponse}
              retailerType={retailerType}
              selectedPeriod={currentPeriod}
              selectedGrowthType={selectedGrowthType}
              factRevenue={factRevenue}
              saleType={saleType}
              isDataChanged={isDataChanged}
              setIsDataChanged={setIsDataChanged}
              onExportReady={onChartExportReady}
              isExportModalOpen={isExportModalOpen}
              volume={volume}
            />
          </div>
        </>
      ),
      noResultHandler: () => setIsExportButtonDisabled(true),
      noResultJsx: <div ref={OpportunityReviewBubbleChartComponent}>{NoResult()}</div>,
      errorMessageJsx: <div ref={OpportunityReviewBubbleChartComponent}>{ErrorResultMessage()}</div>,
      isError: isApiError,
    });
  };

  const displayOpportunityReviewTable = () => {
    return renderVisualization({
      isLoading: loadingSwotTable,
      fetchedApiData: data.swotTableResponse,
      defaultData: [],
      jsxToRender: (
        <div ref={OpportunityReviewBoardComponent}>
          <OpportunityReviewBoard
            tableContent={data.swotTableResponse}
            loadMore={loadMore}
            selectedGrowthType={selectedGrowthType}
            selectedSaleType={saleType}
            factRevenue={factRevenue}
            volume={volume}
            groupContent={groupContent}
            context={context}
            doPreserveSelections={doPreserveSelections}
            setDoPreserveSelections={setDoPreserveSelections}
          />
        </div>
      ),
      noResultHandler: () => setIsExportButtonDisabled(true),
      noResultJsx: <div ref={OpportunityReviewBoardComponent}>{NoResult()}</div>,
      errorMessageJsx: <div ref={OpportunityReviewBoardComponent}>{ErrorResultMessage()}</div>,
      isError: isApiError,
    });
  };

  const headerIconClickHandler = (title: ViewType) => {
    setDisplayTable(title === ViewType.Table);
    if (title !== ViewType.Table) {
      resizeChart();
    }
  };

  const salesTypeChangeHandler = (option: SaleType) => {
    setSaleType(option);
    setDoPreserveSelections(true);
  };

  const retailerTypeChangeHandler = (type: RetailerType) => {
    setRetailerType(type);
  };
  const { Card } = containers;
  const options = buildOptions(featureFilters);
  const countryOptions = sideFiltersState?.appliedFilters[PageType.OPPORTUNITY_REVIEW].selectedFilters.find(
    (f) => f.filterKey === FilterKeys.COUNTRY,
  )?.options;
  const filteredOptions =
    !countryOptions || countryOptions?.length === 1
      ? options
      : options.filter((x) => !multiCountryDisabledFilters.includes(x.filterKey));
  const isLoading = loadingBubbleChart ?? loadingSwotTable;

  const defaultBrandOption = {
    dataType: 'BRAND',
    feature: 'Brand',
    filterKey: FilterKeys.BRAND,
    label: 'Brand',
    value: 'Brand',
    isDisabled: isLoading,
  };
  return (
    <Card>
      <Card.Title title={''}>
        <Card.Content>
          <div
            key={`${feature}-${loadingBubbleChart}-${featureFilters}-${featureValue}`} //NOSONAR typescript:S6551
            data-testid="show-selector"
          >
            <WithHiddenListBox
              options={filteredOptions.length === 0 ? [defaultBrandOption] : filteredOptions}
              isDisabled={isLoading}
              label={'Show'}
              value={featureValue}
              onChange={(option) => {
                setFeatureValue(option.value);
              }}
            />
          </div>
          <div id="sale-selector">
            <OpportunityReviewFilter
              selectedSalesType={saleType}
              onSalesTypeChange={salesTypeChangeHandler}
              chartName={chartName}
              factRevenue={factRevenue}
              volume={volume}
              isDisabled={isLoading}
            />
          </div>
          <div>
            {!displayTable ? (
              <OpportunityReviewChartFilter
                selectedRetailerType={retailerType}
                onRetailerTypeChange={retailerTypeChangeHandler}
                factRevenue={factRevenue}
                isDisabled={isLoading}
              />
            ) : null}
          </div>
        </Card.Content>
        <Card.Title.Actions>
          <OpportunityReviewHeaderIcon
            displayTable={displayTable}
            onChartDownload={onChartDownload}
            isExportButtonDisabled={isExportButtonDisabled ?? isLimitPG}
            saleType={saleType}
            setIsExportModalOpen={setIsExportModalOpen}
            volume={volume}
            createInsightDataFn={createInsightDataFn}
            onClick={headerIconClickHandler}
            createViewStateFn={createViewState}
          />
        </Card.Title.Actions>
      </Card.Title>
      {false}

      {displayContent()}
    </Card>
  );
};

export default OpportunityReviewCard;
